/**
 * Kanonische Zeit-/Saldo-Berechnung — EINE Quelle der Wahrheit für Ist, Soll,
 * Pausen, Absence-Credit und Carryover. Vorher lief das in 4 Backend-Engines
 * (reports /range, /compliance, /team-year-summary, time-accounts) + dem
 * Frontend (calendar-overlays) auseinander (Overlap-Merge, Pausenschwelle 15
 * vs 30 min, UTC-vs-lokal, Anfahrt/Meetings, CREDIT_TYPES, Carryover-Verfall).
 *
 * Reine Funktionen ohne DB-Abhängigkeit: alle Werte werden als Parameter
 * reingereicht, damit apps/api UND apps/web dieselbe Lib nutzen können
 * (packages/shared wird ohne Build-Step direkt als TS konsumiert).
 *
 * Zeit-Konvention: Zeitstempel als epoch-ms (number). Kalendertage als
 * ISO-Datum 'YYYY-MM-DD' (UTC-Kalendertag) — die kanonische Wahl, damit
 * Tagesgrenzen nicht je Codepfad zwischen UTC und Serverzeit springen.
 */

// ── Pausenregel ────────────────────────────────────────────────────────────

/** Eine getrackte Lücke ab dieser Länge gilt als genommene Pause (einheitlich
 *  15 min — vorher wich nur team-year-summary mit 30 min ab). */
export const MIN_BREAK_GAP_MIN = 15

export interface BreakTier {
  /** ab dieser Brutto-Arbeitszeit (Minuten, strikt größer) … */
  afterMin: number
  /** … sind so viele Pausenminuten Pflicht. */
  breakMin: number
}

/** Gesetzlicher DE-Default (ArbZG): >6h → 30 min, >9h → 45 min. */
export const DEFAULT_BREAK_TIERS: BreakTier[] = [
  { afterMin: 6 * 60, breakMin: 30 },
  { afterMin: 9 * 60, breakMin: 45 },
]

/** Pflicht-Pausenminuten für eine gegebene Brutto-Arbeitszeit (höchste
 *  erfüllte Stufe gewinnt). */
export function requiredBreakMinutes(
  grossWorkedMin: number,
  tiers: BreakTier[] = DEFAULT_BREAK_TIERS,
): number {
  let required = 0
  for (const t of tiers) {
    if (grossWorkedMin > t.afterMin && t.breakMin > required) required = t.breakMin
  }
  return required
}

// ── Intervalle / Ist-Zeit ──────────────────────────────────────────────────

export interface Interval {
  start: number // epoch ms
  end: number // epoch ms
}

/** Überlappende/berührende Intervalle zu disjunkten Intervallen mergen
 *  (sortiert nach Start). Leere/negative Intervalle werden verworfen. */
export function mergeIntervals(intervals: Interval[]): Interval[] {
  const valid = intervals.filter((i) => i.end > i.start).sort((a, b) => a.start - b.start)
  const out: Interval[] = []
  for (const iv of valid) {
    const last = out[out.length - 1]
    if (last && iv.start <= last.end) {
      if (iv.end > last.end) last.end = iv.end
    } else {
      out.push({ ...iv })
    }
  }
  return out
}

/** Summe der Minuten disjunkt-gemergter Intervalle (verhindert Doppelzählung
 *  überlappender Einträge). */
export function mergedMinutes(intervals: Interval[]): number {
  return mergeIntervals(intervals).reduce((sum, iv) => sum + (iv.end - iv.start) / 60_000, 0)
}

export interface EntryLike extends Interval {
  /** Anfahrtszeit in Minuten (additiv, kein Intervall). */
  travelToMin?: number | null
  /** Abfahrtszeit in Minuten (additiv). */
  travelFromMin?: number | null
}

export interface ActualOptions {
  /** Meeting-Intervalle, die (union-gemergt mit den Einträgen) mitzählen, wenn countMeetings. */
  meetings?: Interval[]
  /** Anfahrt/Abfahrt in die Ist-Zeit einrechnen (globales Setting, default aus). */
  countTravel?: boolean
  /** Meetings in die Ist-Zeit einrechnen (globales Setting, default aus). */
  countMeetings?: boolean
  /** Pausen vom Ist abziehen (Pflicht minus getrackte Lücken). Default true. */
  deductBreaks?: boolean
  breakTiers?: BreakTier[]
}

export interface ActualResult {
  /** Brutto-Arbeitsminuten (Einträge [+ Meetings] union-gemergt, + Anfahrt). */
  grossMin: number
  /** Bereits als Lücke ≥15 min getrackte Pausenminuten. */
  trackedBreakMin: number
  /** Zusätzlich abgezogene Pflicht-Pausenminuten (max(0, required − tracked)). */
  breakDeductedMin: number
  /** Netto-Ist-Minuten = gross − breakDeducted. */
  netMin: number
}

/**
 * Kanonische Ist-Zeit eines Tages. Einträge (+ optional Meetings) werden
 * union-gemergt (kein Doppelzählen von Überlappungen), Anfahrt/Abfahrt optional
 * additiv, und die gesetzliche Pause abgezogen, soweit sie nicht schon als
 * Lücke ≥15 min zwischen Einträgen genommen wurde.
 */
export function dayActualMinutes(entries: EntryLike[], opts: ActualOptions = {}): ActualResult {
  const deductBreaks = opts.deductBreaks !== false
  const entryIntervals: Interval[] = entries.map((e) => ({ start: e.start, end: e.end }))
  const workIntervals =
    opts.countMeetings && opts.meetings?.length
      ? [...entryIntervals, ...opts.meetings]
      : entryIntervals
  const merged = mergeIntervals(workIntervals)
  let grossMin = merged.reduce((s, iv) => s + (iv.end - iv.start) / 60_000, 0)

  if (opts.countTravel) {
    for (const e of entries) grossMin += (e.travelToMin ?? 0) + (e.travelFromMin ?? 0)
  }

  // Getrackte Pausen = Lücken ≥15 min zwischen aufeinanderfolgenden gemergten
  // Arbeits-Intervallen (nur Einträge, nicht Anfahrt).
  let trackedBreakMin = 0
  const entryMerged = mergeIntervals(entryIntervals)
  for (let i = 1; i < entryMerged.length; i++) {
    const gap = (entryMerged[i].start - entryMerged[i - 1].end) / 60_000
    if (gap >= MIN_BREAK_GAP_MIN) trackedBreakMin += gap
  }

  let breakDeductedMin = 0
  if (deductBreaks) {
    const required = requiredBreakMinutes(grossMin, opts.breakTiers)
    breakDeductedMin = Math.max(0, required - trackedBreakMin)
  }

  return {
    grossMin,
    trackedBreakMin,
    breakDeductedMin,
    netMin: grossMin - breakDeductedMin,
  }
}

// ── Soll / Work-Schedule-Auflösung ─────────────────────────────────────────

export interface ScheduleLike {
  validFrom: string // 'YYYY-MM-DD'
  validTo: string | null // 'YYYY-MM-DD' | null
  monHours: number | string | null
  tueHours: number | string | null
  wedHours: number | string | null
  thuHours: number | string | null
  friHours: number | string | null
  satHours: number | string | null
  sunHours: number | string | null
}

// Sonntag=0 … Samstag=6 (getUTCDay-Reihenfolge).
const WEEKDAY_FIELDS: (keyof ScheduleLike)[] = [
  'sunHours',
  'monHours',
  'tueHours',
  'wedHours',
  'thuHours',
  'friHours',
  'satHours',
]

/** Aktiven Schedule für einen Kalendertag bestimmen: gültig am Tag, jüngstes
 *  validFrom gewinnt. Kein passender ⇒ null (Soll 0, kein Phantom-Saldo). */
export function resolveSchedule<T extends ScheduleLike>(schedules: T[], dateISO: string): T | null {
  let best: T | null = null
  for (const s of schedules) {
    if (s.validFrom <= dateISO && (s.validTo == null || s.validTo >= dateISO)) {
      if (!best || s.validFrom > best.validFrom) best = s
    }
  }
  return best
}

/** Tages-Soll in Minuten für einen (UTC-)Kalendertag; Feiertag reduziert
 *  (halber Feiertag → halbes Soll, ganzer → 0). */
export function dailyTargetMinutes(
  schedule: ScheduleLike | null,
  dateISO: string,
  holiday?: { halfDay?: boolean } | null,
): number {
  if (!schedule) return 0
  const dow = new Date(`${dateISO}T00:00:00Z`).getUTCDay()
  const raw = schedule[WEEKDAY_FIELDS[dow]]
  const hours = typeof raw === 'string' ? Number.parseFloat(raw) : (raw ?? 0)
  let minutes = (Number.isFinite(hours) ? hours : 0) * 60
  if (holiday) minutes = holiday.halfDay ? minutes * 0.5 : 0
  return minutes
}

// ── Abwesenheiten ──────────────────────────────────────────────────────────

/** Abwesenheits-Typen, die aufs Soll angerechnet werden (Credit). Bewusst OHNE
 *  overtime_reduction/compensation: Überstundenabbau muss den Saldo mindern,
 *  nicht neutralisieren. */
export const CREDIT_ABSENCE_TYPES = ['vacation', 'sick', 'training', 'other'] as const
export type CreditAbsenceType = (typeof CREDIT_ABSENCE_TYPES)[number]

export function isCreditType(type: string): boolean {
  return (CREDIT_ABSENCE_TYPES as readonly string[]).includes(type)
}

export interface AbsenceLike {
  fromDate: string // 'YYYY-MM-DD'
  toDate: string // 'YYYY-MM-DD'
  type: string
  status: string
  firstDayHalf?: string | null
  lastDayHalf?: string | null
}

/**
 * Anteil (0, 0.5, 1) den eine Abwesenheit an einem Kalendertag belegt.
 * Berücksichtigt Halbtage am ersten/letzten Tag. 0, wenn der Tag außerhalb der
 * Spanne liegt oder die Abwesenheit nicht approved ist.
 */
export function absenceFractionForDay(absence: AbsenceLike, dateISO: string): number {
  if (absence.status !== 'approved') return 0
  if (dateISO < absence.fromDate || dateISO > absence.toDate) return 0
  const isFirst = dateISO === absence.fromDate
  const isLast = dateISO === absence.toDate
  if (isFirst && isLast) {
    return absence.firstDayHalf ? 0.5 : 1
  }
  if (isFirst) return absence.firstDayHalf ? 0.5 : 1
  if (isLast) return absence.lastDayHalf ? 0.5 : 1
  return 1
}

/** Überschneidungstage einer Abwesenheit auf ein Jahr geclippt (verhindert
 *  Doppelzählung jahresübergreifender Abwesenheiten in beiden Jahren). Gibt
 *  from/to als ISO-String zurück oder null, wenn keine Überschneidung. */
export function clipAbsenceToYear(
  absence: AbsenceLike,
  year: number,
): { fromDate: string; toDate: string } | null {
  const yearStart = `${year}-01-01`
  const yearEnd = `${year}-12-31`
  const from = absence.fromDate > yearStart ? absence.fromDate : yearStart
  const to = absence.toDate < yearEnd ? absence.toDate : yearEnd
  if (from > to) return null
  return { fromDate: from, toDate: to }
}

/**
 * Heutiges Datum als ISO-Kalendertag in einer Zeitzone (Default Europe/Berlin).
 * Für den „bis heute"-Soll-Cutoff: die UTC-Tagesgrenze ließ den neuen Tag erst
 * um 01:00 (CET) bzw. 02:00 (CEST) lokal beginnen — Sollstunden des
 * angebrochenen Tages fehlten bis dahin. en-CA formatiert als YYYY-MM-DD.
 */
export function localTodayISO(timeZone = 'Europe/Berlin', at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at)
}

/** Nächster UTC-Kalendertag als ISO-String. */
export function nextDayISO(dateISO: string): string {
  const d = new Date(`${dateISO}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

export interface CountAbsenceDaysOptions {
  /** Feiertage des Users: ISO-Datum → halfDay. Ein Voll-Feiertag verbraucht
   *  keinen Abwesenheitstag (Fraction 0 — der Tag ist ohnehin frei); ein
   *  Halbtags-Feiertag lässt die Fraction unverändert (Volltag-Urlaub
   *  verbraucht 1 — Clockodo-Verhalten). */
  holidays?: ReadonlyMap<string, { halfDay: boolean }>
  /** Roh-Tages-Soll in Minuten (OHNE Feiertagsabzug — den macht die
   *  holidays-Map). Ein Tag mit Soll 0 ist kein Werktag des Users und
   *  verbraucht keinen Abwesenheitstag (Teilzeit). Ohne Callback gilt
   *  generisch Mo–Fr. */
  targetMinutesForDay?: (dateISO: string) => number
  /** Aufs Jahr clippen: jahresübergreifende Abwesenheiten zählen nur mit
   *  ihren Tagen IM Jahr (sonst Doppelzählung in beiden Jahren). Halbtags-
   *  Flags bleiben dabei auf den Original-Grenzen der Abwesenheit. */
  year?: number
  /** Nur Tage bis EINSCHLIESSLICH dieses ISO-Datums zählen — für den
   *  „Genommen"-Split (genehmigte Tage bis heute). Ein heute laufender
   *  Urlaub wird damit tag-genau geteilt. */
  until?: string
  /** Nur Tage AB diesem ISO-Datum zählen (inklusive) — das Gegenstück für
   *  „Geplant" (genehmigte Zukunftstage): from = Tag nach dem Stichtag. */
  from?: string
}

/**
 * Kanonischer Abwesenheits-Tage-Zähler (Urlaub, Krank, Sonstiges). Die
 * Status-Filterung (approved/pending) ist bewusst Sache des Aufrufers — der
 * Zähler zählt alles, was er bekommt (Stundenkonto braucht approved UND
 * pending getrennt). Vorher existierten zwei divergierende Zähler:
 * time-accounts (Jahres-Clipping, aber feiertags- und schedule-blind) und
 * der Team-Jahresreport (feiertags-/schedule-aware, aber ohne Clipping).
 */
export function countAbsenceDays(
  absences: AbsenceLike[],
  opts: CountAbsenceDaysOptions = {},
): number {
  let total = 0
  for (const a of absences) {
    let from = a.fromDate
    let to = a.toDate
    if (opts.year != null) {
      const clip = clipAbsenceToYear(a, opts.year)
      if (!clip) continue
      from = clip.fromDate
      to = clip.toDate
    }
    if (opts.from != null && opts.from > from) from = opts.from
    if (opts.until != null && opts.until < to) to = opts.until
    if (from > to) continue
    const singleDay = a.fromDate === a.toDate
    for (let cur = from; cur <= to; cur = nextDayISO(cur)) {
      let isWorkday: boolean
      if (opts.targetMinutesForDay) {
        isWorkday = opts.targetMinutesForDay(cur) > 0
      } else {
        const wd = new Date(`${cur}T00:00:00Z`).getUTCDay()
        isWorkday = wd !== 0 && wd !== 6
      }
      if (!isWorkday) continue

      // Halbtags-Flags gelten für die ORIGINAL-Grenzen der Abwesenheit — ein
      // weggeclippter erster Tag überträgt sein 0.5 nicht auf den Jahresanfang.
      let frac = 1
      if (singleDay) {
        if (a.firstDayHalf) frac = 0.5
      } else if (cur === a.fromDate && a.firstDayHalf) {
        frac = 0.5
      } else if (cur === a.toDate && a.lastDayHalf) {
        frac = 0.5
      }

      const holiday = opts.holidays?.get(cur)
      if (holiday && !holiday.halfDay) frac = 0
      total += frac
    }
  }
  return total
}

// ── Carryover / Urlaubsübertrag ────────────────────────────────────────────

export interface CarryoverAdjustmentLike {
  type: string
  days: number | string
  /** Verfallsdatum (default 31.03. Folgejahr). null = kein Verfall. */
  expiresAt?: string | null
}

/**
 * Summe der wirksamen Urlaubs-Adjustment-Tage zum Stichtag. Carryover-Zeilen,
 * deren expiresAt vor dem Stichtag liegt, sind verfallen und zählen NICHT mehr
 * (vorher wurde der Verfall nirgends durchgesetzt).
 */
export function activeAdjustmentDays(
  adjustments: CarryoverAdjustmentLike[],
  asOfISO: string,
): number {
  let total = 0
  for (const a of adjustments) {
    const days = typeof a.days === 'string' ? Number.parseFloat(a.days) : a.days
    if (!Number.isFinite(days)) continue
    if (a.expiresAt && a.expiresAt < asOfISO) continue // verfallen
    total += days
  }
  return total
}
